import { useEffect, useState } from "react";
import { ApiError, listTenants, type TenantSummary } from "../api/client";

/**
 * Screen 09 — certification body applications, and which of them is waiting.
 *
 * The queue and the register (screen 71) read the same list and are not the same screen.
 * The register is about the door: who registered, and may they sign in. This is about the
 * accreditation: what has been claimed, and has anyone checked it. A row here opens the
 * application; a row there carries the activation toggle.
 *
 * Only rows with a submitted application can be verified, so a registered body that has
 * typed nothing yet shows its standing and does not open — there is nothing behind it to
 * assess, and the server answers a verification of one with a 409 saying so.
 */
export function VerificationQueue({ onOpen }: { onOpen: (tenantId: string) => void }) {
  const [tenants, setTenants] = useState<TenantSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listTenants()
      .then(setTenants)
      .catch((cause: unknown) =>
        setError(cause instanceof ApiError ? cause.message : "The queue could not be read."),
      );
  }, []);

  const waiting = (tenants ?? []).filter(
    (tenant) => tenant.onboarding_complete && tenant.verified_at === null,
  ).length;

  return (
    <>
      <div className="eyebrow">Complao admin</div>
      <h1>Verification queue</h1>
      <p className="sub" style={{ marginBottom: "18px" }}>
        Certification body applications. No body can configure an audit until its
        accreditation is verified.
      </p>

      {error ? (
        <p className="problem" role="alert">
          {error}
        </p>
      ) : null}

      <div className="callout">
        <b>
          {waiting === 1
            ? "1 application awaiting verification."
            : `${waiting} applications awaiting verification.`}
        </b>{" "}
        Check the accreditation number and scope against the accreditation body&rsquo;s
        public register before activating.
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <div className="tscroll">
          <table>
            <thead>
              <tr>
                <th>Certification body</th>
                <th>Accreditation</th>
                <th>Scopes claimed</th>
                <th>Status</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {tenants === null ? (
                <tr>
                  <td colSpan={5}>Reading the queue&hellip;</td>
                </tr>
              ) : tenants.length === 0 ? (
                <tr>
                  <td colSpan={5}>No certification body has registered yet.</td>
                </tr>
              ) : (
                tenants.map((tenant) => (
                  <tr
                    key={tenant.id}
                    className={tenant.onboarding_complete ? "click" : undefined}
                    onClick={
                      tenant.onboarding_complete ? () => onOpen(tenant.id) : undefined
                    }
                  >
                    <td>
                      <b>{tenant.trading_name ?? tenant.legal_name ?? tenant.contact_email}</b>
                      {tenant.legal_name ? (
                        <>
                          <br />
                          <span className="sub">{tenant.legal_name}</span>
                        </>
                      ) : null}
                    </td>
                    <td>
                      {tenant.accreditation_body ? (
                        <>
                          {tenant.accreditation_body}{" "}
                          <span className="mono">{tenant.accreditation_number}</span>
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      {(tenant.standards_claimed ?? []).length > 0
                        ? (tenant.standards_claimed ?? []).join(" · ")
                        : "—"}
                    </td>
                    <td>
                      <Standing tenant={tenant} />
                    </td>
                    <td className="sub">{formatDate(tenant.verified_at ?? tenant.registered_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

/**
 * Three answers, and they are three because the middle one is not the other two.
 *
 * "Not submitted" is the body's move. "Pending verification" is Complao's. "Verified" is
 * the Cl. 5.2 decision, and it is deliberately not the same badge as an active sign-in —
 * an activated body that nobody has assessed must not read as an assessed one.
 */
function Standing({ tenant }: { tenant: TenantSummary }) {
  if (tenant.verified_at !== null) return <span className="pill p-ok">Verified</span>;
  if (!tenant.onboarding_complete) return <span className="pill p-mute">Not submitted</span>;
  return <span className="pill p-wait">Pending verification</span>;
}

function formatDate(value: string): string {
  const when = new Date(value);
  return Number.isNaN(when.getTime())
    ? "—"
    : when.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}
